import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FlowToolsService } from '@core/services/flow-tools/flow-tools.service';

/**
 * Ejecuta en el CMS la misma lógica que dispara el WhatsApp Flow "Flyers"
 * (FLYERS_TOKEN) — ver chatbot_v1/flows/FlowFlyers.js y
 * worker_flyers/helpers/getData.js (buildFlyerData) — llamando directo a
 * POST /chatBot/generate-flyer, sin pasar por WhatsApp.
 *
 * Limitación conocida: las fotos del flyer y el logo (`photosFLayer_1`,
 * `photoslogo`) solo se pueden enviar como IDs de media de WhatsApp
 * (obtenidos al subir la imagen dentro de un WhatsApp Flow real — ver
 * worker_flyers/whatsappService/whatsappServiceBot.js#fetchAllPhotosBase64,
 * que solo acepta un Meta media ID o un cdn_url cifrado, nunca una URL
 * pública). Por eso aquí son un campo JSON avanzado y opcional: sin fotos el
 * flyer se genera igual, solo sin las imágenes.
 */
@Component({
  selector: 'app-flyers-tester',
  templateUrl: './flyers-tester.component.html',
  styleUrls: ['./flyers-tester.component.scss'],
})
export class FlyersTesterComponent implements OnDestroy {
  form: FormGroup;
  isLoading = false;
  loadingLabel = 'Generando...';
  errorMessage = '';
  resultUrl: string | null = null;

  private pollTimer: any = null;
  private readonly POLL_INTERVAL_MS = 2500;
  private readonly POLL_TIMEOUT_MS = 60000;

  categoryOptions = [
    { value: 'A', label: 'Moda / Confección' },
    { value: 'B', label: 'Servicios del hogar' },
    { value: 'C', label: 'Belleza' },
    { value: 'D', label: 'Todo / General' },
  ];

  designOptions = [1, 2, 3, 4];

  constructor(
    private fb: FormBuilder,
    private flowToolsService: FlowToolsService,
  ) {
    this.form = this.fb.group({
      category: ['C', Validators.required],
      design: [1, Validators.required],
      flyerSize: ['POST', Validators.required],
      colorPrimaryPastel: ['FFB71B'],
      title1: [''],
      subtitle1: [''],
      textMain: [''],
      short_bullet_1: [''],
      short_bullet_2: [''],
      short_bullet_3: [''],
      short_bullet_4: [''],
      short_bullet_5: [''],
      short_bullet_6: [''],
      contactFacebook: [''],
      contactInstagram: [''],
      contactCity: [''],
      phoneList: ['NO'],
      // Avanzado / opcional: JSON de media IDs reales de WhatsApp.
      photosFLayer_1_json: [''],
      photoslogo_json: [''],
    });
  }

  private buildFlyerPath(): string {
    const category = this.form.get('category')?.value || 'C';
    const design = this.form.get('design')?.value || 1;
    return `flyer_${design}${category}`;
  }

  private categoryName(): string {
    const found = this.categoryOptions.find(
      (c) => c.value === this.form.get('category')?.value,
    );
    return found?.label || '';
  }

  onSubmit(): void {
    this.isLoading = true;
    this.loadingLabel = 'Encolando...';
    this.errorMessage = '';
    this.resultUrl = null;

    // La generación es asíncrona (backend_v1 encola en 'flyerQueue', el worker
    // la procesa y avisa por callback) — se usa un "phone" sintético como
    // identificador del job para poder consultarlo por polling después.
    const testPhone = `cms-test-${Date.now()}`;

    const raw = this.form.value;
    const payload: Record<string, any> = {
      phone: testPhone,
      flyerPathName: this.categoryName(),
      flyerPath: this.buildFlyerPath(),
      flyerSize: raw.flyerSize,
      colorPrimaryPastel: raw.colorPrimaryPastel,
      hasTitle: !!raw.title1,
      title1: raw.title1,
      hasSubtitle: !!raw.subtitle1,
      subtitle1: raw.subtitle1,
      textMain: raw.textMain,
      contactFacebook: raw.contactFacebook,
      contactInstagram: raw.contactInstagram,
      contactCity: raw.contactCity,
      phoneList: raw.phoneList,
      enhancementdGpt: 'NO',
    };

    const bullets = [1, 2, 3, 4, 5, 6]
      .map((i) => raw[`short_bullet_${i}`])
      .filter((v) => !!v);
    if (bullets.length > 0) {
      payload['hasShortBullets'] = true;
      payload['shortBulletCount'] = bullets.length;
      bullets.forEach((text, idx) => {
        payload[`short_bullet_${idx + 1}`] = text;
      });
    }

    if (raw.photosFLayer_1_json) {
      try {
        payload['photosFLayer_1'] = JSON.parse(raw.photosFLayer_1_json);
      } catch {
        this.isLoading = false;
        this.errorMessage = 'El JSON de fotos del flyer no es válido.';
        return;
      }
    }
    if (raw.photoslogo_json) {
      try {
        payload['photoslogo'] = JSON.parse(raw.photoslogo_json);
      } catch {
        this.isLoading = false;
        this.errorMessage = 'El JSON del logo no es válido.';
        return;
      }
    }

    this.flowToolsService.generateFlyer(payload as any).subscribe(
      (response) => {
        if (!response.ok) {
          this.isLoading = false;
          this.errorMessage = response.error || response.details || 'No se pudo encolar el flyer.';
          return;
        }
        this.loadingLabel = 'Generando (puede tardar unos segundos)...';
        this.pollStatus(testPhone, Date.now());
      },
      (error) => {
        this.isLoading = false;
        this.errorMessage = `Error: ${error.error?.details || error.error?.error || error.message}`;
      },
    );
  }

  /** Hace polling de GET /chatBot/flyer-status/:phone hasta 'done'/'failed' o timeout. */
  private pollStatus(phone: string, startedAt: number): void {
    this.pollTimer = setTimeout(() => {
      this.flowToolsService.getFlyerStatus(phone).subscribe(
        (status) => {
          if (status.status === 'done' && status.url) {
            this.isLoading = false;
            this.resultUrl = status.url;
            return;
          }
          if (status.status === 'failed') {
            this.isLoading = false;
            this.errorMessage = status.error || 'La generación del flyer falló.';
            return;
          }
          if (Date.now() - startedAt > this.POLL_TIMEOUT_MS) {
            this.isLoading = false;
            this.errorMessage =
              'El flyer sigue procesándose. Revisa el worker (worker_flyers) — puede que esté caído o que Redis no esté disponible.';
            return;
          }
          this.pollStatus(phone, startedAt);
        },
        () => {
          this.isLoading = false;
          this.errorMessage = 'No se pudo consultar el estado del flyer.';
        },
      );
    }, this.POLL_INTERVAL_MS);
  }

  reset(): void {
    this.form.reset({
      category: 'C',
      design: 1,
      flyerSize: 'POST',
      colorPrimaryPastel: 'FFB71B',
      phoneList: 'NO',
    });
    this.resultUrl = null;
    this.errorMessage = '';
    this.isLoading = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }
}
